// useIntelGeomlessResults — fetch the matched signals WITHOUT the map's
// has_geom filter, so the map view can dock a results list for filters whose
// matches are entity-keyed (e.g. OFAC sanctions: ~968k rows, no coordinates).
//
// The map feed (useIntelFeed) pulls has_geom:true, so those rows never reach
// the map and the operator would see an empty map for a filter that matches a
// million records. This hook is gated (`enabled`) so it only fires when the
// route detects that case — no extra request on a normally-plottable filter.

import { API_BASE_URL } from '@/lib/env';
import { readAccessToken } from '@/state/auth';
import type { filters as f, intel as i } from '@eidollon/contracts';
import { useQuery } from '@tanstack/react-query';

const EMPTY: i.IntelSignal[] = [];

export function useIntelGeomlessResults(
  state: f.FilterState,
  enabled: boolean,
  limit = 100,
): { rows: i.IntelSignal[]; isLoading: boolean } {
  const token = readAccessToken() ?? '';

  const query = useQuery({
    queryKey: ['intel-geomless-results', state, limit, token],
    enabled,
    queryFn: async (): Promise<i.IntelSignal[]> => {
      const res = await fetch(`${API_BASE_URL}/v1/intel/signals`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        // No has_geom → entity-keyed rows (sanctions) are included.
        body: JSON.stringify({ state, limit }),
      });
      if (!res.ok) throw new Error(`intel geomless results ${res.status}`);
      const json = (await res.json()) as { items?: i.IntelSignal[] };
      return json.items ?? [];
    },
    staleTime: 60_000,
  });

  return { rows: query.data ?? EMPTY, isLoading: enabled && query.isLoading };
}
